import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { signOut, onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebase';
import { useNavigate, Link } from 'react-router-dom';
import { getUserInfo } from '../services/userService';
import { listJobs, formatTimestamp, formatFrequency, formatAllocationType, formatAllocationDetails, getGoogleMapsUrl, listJobSchedules, submitQuote, getJobCounts } from '../services/jobService';
import { searchHouseholds } from '../services/householdService';
import { useConfig } from '../context/ConfigContext';
import StatusBadge from '../components/StatusBadge';
import PendingQuotationList from '../components/PendingQuotationList';
import ComplianceTab from '../components/ComplianceTab';
import { debounce } from 'lodash';
import { FaCalendarAlt, FaClock, FaUser, FaHome, FaMapMarkerAlt, FaTasks, FaRedo, FaTags, FaHourglassHalf, FaCommentAlt, FaStickyNote, FaCalendarCheck, FaFilter, FaTimes, FaExclamationTriangle, FaCheckCircle, FaDollarSign, FaInfoCircle, FaUserTie, FaLayerGroup, FaFileInvoiceDollar, FaSignOutAlt } from 'react-icons/fa';
import { MdOutlineImageNotSupported } from 'react-icons/md';
import { terminology } from '../config/terminology';
import { isDemoMode } from '../utils/demoMode';

// --- useSearch Hook ---
const useSearch = (searchFunction, options = {}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const optionsString = JSON.stringify(options);

  const debouncedFetch = useCallback(
    debounce(async (term, fetchOptions) => {
      if (!searchFunction || term.length < 3) {
        setSuggestions([]);
        setShowSuggestions(false);
        return;
      }
      setIsSearching(true);
      try {
        const data = await searchFunction({ searchString: term, ...fetchOptions });
        const results = data.households || data.clients || data.franchisees || [];
        setSuggestions(results);
        setShowSuggestions(true);
      } catch (error) {
        console.error('Search error:', error);
        setSuggestions([]);
        setShowSuggestions(false);
      } finally {
        setIsSearching(false);
      }
    }, 300),
    [searchFunction]
  );

  useEffect(() => {
    const currentOptions = JSON.parse(optionsString);
    debouncedFetch(searchTerm, currentOptions);
    return () => debouncedFetch.cancel();
  }, [searchTerm, optionsString, debouncedFetch]);

  return { searchTerm, setSearchTerm, suggestions, setSuggestions, isSearching, showSuggestions, setShowSuggestions };
};
// --- End useSearch Hook ---

const Home = () => {
  const navigate = useNavigate();
  const { statuses: configStatuses, loadingConfig, configError } = useConfig();
  const [userInfo, setUserInfo] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [loadingUser, setLoadingUser] = useState(true);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [userError, setUserError] = useState(null);
  const [jobsError, setJobsError] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(() => sessionStorage.getItem('home_filter_status') || '');
  const [activeTab, setActiveTab] = useState('myJobs');

  // Add state for quote confirmation modal
  const [showQuoteConfirmation, setShowQuoteConfirmation] = useState(false);
  const [quoteToConfirm, setQuoteToConfirm] = useState(null);

  // --- States for "Pending Your Quotation" (previously Pending Quotes) ---
  const [pendingYourQuotationSchedules, setPendingYourQuotationSchedules] = useState([]);
  const [loadingPendingYourQuotation, setLoadingPendingYourQuotation] = useState(false);
  const [pendingYourQuotationError, setPendingYourQuotationError] = useState(null);
  const [nextPageDocIdPendingYourQuotation, setNextPageDocIdPendingYourQuotation] = useState(null);
  const [initialPendingYourQuotationLoaded, setInitialPendingYourQuotationLoaded] = useState(false);

  // --- States for "Pending Approval" ---
  const [activeQuoteSubTab, setActiveQuoteSubTab] = useState('pendingYourQuotation'); // 'pendingYourQuotation' or 'pendingApproval'
  const [pendingApprovalSchedules, setPendingApprovalSchedules] = useState([]);
  const [loadingPendingApproval, setLoadingPendingApproval] = useState(false);
  const [pendingApprovalError, setPendingApprovalError] = useState(null);
  const [nextPageDocIdPendingApproval, setNextPageDocIdPendingApproval] = useState(null);
  const [initialPendingApprovalLoaded, setInitialPendingApprovalLoaded] = useState(false);

  // --- Welcome message counts (independent of filter/pagination state) ---
  const [welcomeApprovedCount, setWelcomeApprovedCount] = useState(null);
  const [welcomePendingQuoteCount, setWelcomePendingQuoteCount] = useState(null);

  // Add state for price per hour inputs
  const [pricePerHourInputs, setPricePerHourInputs] = useState({});
  const [quoteSubmitError, setQuoteSubmitError] = useState({}); // State for individual quote errors
  const [isSubmittingQuote, setIsSubmittingQuote] = useState({}); // State for individual quote submission loading

  // Add state for Notes for Admin inputs
  const [notesForAdminInputs, setNotesForAdminInputs] = useState({});

  // Add state for Frequency inputs
  const [frequencyInputs, setFrequencyInputs] = useState({});

  // Update price per hour for a specific schedule
  const handlePricePerHourChange = (scheduleId, value) => {
    setPricePerHourInputs(prev => ({
      ...prev,
      [scheduleId]: value
    }));
  };

  // Update notes for admin for a specific schedule
  const handleNotesForAdminChange = (scheduleId, value) => {
    setNotesForAdminInputs((prev) => ({ ...prev, [scheduleId]: value }));
  };

  // Update frequency for a specific schedule
  const handleFrequencyChange = (scheduleId, field, value) => {
    setFrequencyInputs((prev) => ({
      ...prev,
      [scheduleId]: {
        ...prev[scheduleId],
        [field]: value,
        // Auto-select interval option when user types in interval fields
        ...(field === 'intervalValue' || field === 'intervalUnit' ? { option: 'interval' } : {})
      }
    }));
  };

  // Initialize price per hour inputs when schedules change (for 'pendingYourQuotation')
  useEffect(() => {
    const initialPrices = {};
    pendingYourQuotationSchedules.forEach(schedule => {
      initialPrices[schedule.id] = schedule.pricePerHour || '';
    });
    setPricePerHourInputs(initialPrices);
  }, [pendingYourQuotationSchedules]);

  // Initialize notes for admin inputs when schedules change (for 'pendingYourQuotation')
  useEffect(() => {
    const initialNotes = {};
    pendingYourQuotationSchedules.forEach((s) => {
      initialNotes[s.id] = '';
    });
    setNotesForAdminInputs(initialNotes);
  }, [pendingYourQuotationSchedules]);

  // Initialize frequency inputs when schedules change (for 'pendingYourQuotation')
  useEffect(() => {
    const initialFrequency = {};
    pendingYourQuotationSchedules.forEach((schedule) => {
      // Initialize from existing data or empty values
      const existingFrequencyType = schedule.frequencyType?.toLowerCase();
      const existingFrequencyValue = schedule.frequencyValue;
      
      if (existingFrequencyType === 'monthly') {
        initialFrequency[schedule.id] = {
          option: 'monthly',
          intervalValue: '',
          intervalUnit: 'days'
        };
      } else if (existingFrequencyType === 'days' || existingFrequencyType === 'weeks') {
        initialFrequency[schedule.id] = {
          option: 'interval',
          intervalValue: existingFrequencyValue || '',
          intervalUnit: existingFrequencyType
        };
      } else {
        // No existing frequency data, start empty
        initialFrequency[schedule.id] = {
          option: '',
          intervalValue: '',
          intervalUnit: 'days'
        };
      }
    });
    setFrequencyInputs(initialFrequency);
  }, [pendingYourQuotationSchedules]);

  // --- Pagination State for My Jobs ---
  const [nextPageDocId, setNextPageDocId] = useState(null);
  const [initialJobsLoaded, setInitialJobsLoaded] = useState(false); // Track if initial jobs load completed

  // --- Filter State ---
  const [filterHouseholdId, setFilterHouseholdId] = useState(() => sessionStorage.getItem('home_filter_householdId') || null);
  const [noMoreJobsMessage, setNoMoreJobsMessage] = useState(''); // Added for no more jobs message
  const [householdSearchTerm, setHouseholdSearchTerm] = useState(() => sessionStorage.getItem('home_filter_householdSearchTerm') || ''); // Displayed search term
  const householdSearch = useSearch(searchHouseholds); // Hook for household search
  const filterDropdownRef = useRef(null); // Ref for closing dropdowns
  // --- End Filter State ---

  // Persist filter state across navigation
  useEffect(() => {
    if (selectedStatus) sessionStorage.setItem('home_filter_status', selectedStatus);
    else sessionStorage.removeItem('home_filter_status');
    if (filterHouseholdId) sessionStorage.setItem('home_filter_householdId', filterHouseholdId);
    else sessionStorage.removeItem('home_filter_householdId');
    if (householdSearchTerm) sessionStorage.setItem('home_filter_householdSearchTerm', householdSearchTerm);
    else sessionStorage.removeItem('home_filter_householdSearchTerm');
  }, [selectedStatus, filterHouseholdId, householdSearchTerm]);

  // Memoize the status options for the filter dropdown, adding "All Statuses"
  const statusFilterOptions = useMemo(() => {
    // Filter out 'pending-quote' and 'pending-approval' statuses from the dropdown only
    const filteredStatuses = configStatuses.filter(s => 
      s.code !== 'pending-quote' && s.code !== 'pending-approval'
    );
    
    // Map the filtered statuses to options
    const options = filteredStatuses.map(s => ({ value: s.code, label: s.display }));
    
    // Add "All Statuses" at the beginning
    return [{ value: '', label: 'All Statuses' }, ...options];
  }, [configStatuses]);

  // For approved jobs, only show the earliest instance per schedule (recurring jobs).
  // Jobs from the same schedule (same scheduleId) that are approved will all be hidden
  // except the earliest one. Non-approved jobs and one-off jobs (no scheduleId) are shown as-is.
  // The API returns jobs sorted by jobDate asc so the first encountered per scheduleId is earliest.
  const displayedJobs = useMemo(() => {
    const seenApprovedScheduleIds = new Set();
    return jobs.filter(job => {
      if (job.status === 'approved' && job.scheduleId) {
        if (seenApprovedScheduleIds.has(job.scheduleId)) return false;
        seenApprovedScheduleIds.add(job.scheduleId);
        return true;
      }
      return true;
    });
  }, [jobs]);

  // Fetch User Info Effect
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setLoadingUser(true);
        setUserError(null);
        try {
          const idToken = await user.getIdToken();
          const fetchedUserInfo = await getUserInfo(idToken);
          setUserInfo(fetchedUserInfo);
        } catch (err) {
          console.error('Failed to fetch user info:', err);
          setUserError(err.message || 'Could not load user data.');
        } finally {
          setLoadingUser(false);
        }
      } else {
        setUserInfo(null);
        setLoadingUser(false);
        navigate('/login');
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  // Fetch Jobs Effect (runs when userInfo, selectedStatus, or filterHouseholdId changes)
  const fetchJobs = useCallback(
    async (filters = {}, loadMore = false) => {
      // Check if userInfo exists and has a userType
      if (!userInfo || !userInfo.userType) {
        console.log("fetchJobs aborted: userInfo or userType missing.");
        return;
      }

      // Check fseId only if user is NOT admin
      if (userInfo.userType !== 'admin' && !userInfo.fseId) {
        console.log("fetchJobs aborted: Non-admin user missing fseId.");
        return;
      }

      console.log("fetchJobs executing for user:", userInfo.uid, "Type:", userInfo.userType, "LoadMore:", loadMore);

      setLoadingJobs(true);
      setJobsError(null);
      if (!loadMore) { // Reset message on initial load or filter change
        setNoMoreJobsMessage('');
      }

      try {
        const options = {
          userType: userInfo.userType,
          // Only include fseId if it exists and user is not admin
          fseId: userInfo.userType !== 'admin' ? userInfo.fseId : undefined,
          pageSize: 10,
          // Add pagination support - only include startAfterDocId if loading more
          startAfterDocId: loadMore ? nextPageDocId : null,
        };

        // Only add filters if they have valid values
        const status = filters.status || selectedStatus;
        const householdId = filters.householdId || filterHouseholdId;
        
        console.log("Filter debug - status:", status, "householdId:", householdId, "selectedStatus:", selectedStatus, "filterHouseholdId:", filterHouseholdId);
        
        if (status && status !== '') {
          options.status = status;
        }
        if (householdId) {
          options.householdId = householdId;
        }
        
        console.log("Calling listJobs with options:", options);
        const data = await listJobs(options);
        
        if (loadMore) {
          if (data.jobs && data.jobs.length > 0) {
            setJobs(prevJobs => [...prevJobs, ...(data.jobs || [])]);
            // Only set nextPageDocId if we got a full page of results
            setNextPageDocId(data.jobs.length >= 10 ? data.nextPageDocId : null);
            setNoMoreJobsMessage(''); // Clear message if new jobs are loaded
          } else {
            setNoMoreJobsMessage('No more jobs to load.');
            setNextPageDocId(null); // Explicitly set to null
          }
        } else { // Initial load or filter change
          setJobs(data.jobs || []);
          // Only set nextPageDocId if we got a full page of results
          setNextPageDocId(data.jobs && data.jobs.length >= 10 ? data.nextPageDocId : null);
          setNoMoreJobsMessage('');
          // Mark initial load as complete (even if 0 results due to filters)
          if (!initialJobsLoaded) {
            setInitialJobsLoaded(true);
          }
        }
        
      } catch (err) {
        console.error('Failed to fetch jobs:', err);
        setJobsError(err.message || 'Could not load jobs.');
        if (!loadMore) {
          setJobs([]);
          setNextPageDocId(null);
          setNoMoreJobsMessage(''); // Clear message on error for initial load
        }
      } finally {
        setLoadingJobs(false);
      }
    },
    // Dependencies: userInfo object itself, selectedStatus, filterHouseholdId, nextPageDocId, initialJobsLoaded
    // nextPageDocId is needed here because fetchJobs uses it to decide the startAfterDocId for loadMore.
    // setNoMoreJobsMessage is a setter, stable.
    [userInfo, selectedStatus, filterHouseholdId, nextPageDocId, initialJobsLoaded]
  );

  // Generalized fetch function for schedules by quote status
  const fetchSchedulesByQuoteStatus = useCallback(async (quoteStatus, loadMore = false) => {
    if (!userInfo || !userInfo.fseId) {
      if (quoteStatus === 'pending-quote') {
        setPendingYourQuotationSchedules([]);
        setNextPageDocIdPendingYourQuotation(null);
        setInitialPendingYourQuotationLoaded(true);
      } else if (quoteStatus === 'pending-approval') {
        setPendingApprovalSchedules([]);
        setNextPageDocIdPendingApproval(null);
        setInitialPendingApprovalLoaded(true);
      }
      return;
    }

    const isLoadingSetter = quoteStatus === 'pending-quote' ? setLoadingPendingYourQuotation : setLoadingPendingApproval;
    const errorSetter = quoteStatus === 'pending-quote' ? setPendingYourQuotationError : setPendingApprovalError;
    const schedulesSetter = quoteStatus === 'pending-quote' ? setPendingYourQuotationSchedules : setPendingApprovalSchedules;
    const nextPageDocIdSetter = quoteStatus === 'pending-quote' ? setNextPageDocIdPendingYourQuotation : setNextPageDocIdPendingApproval;
    const currentNextPageDocId = quoteStatus === 'pending-quote' ? nextPageDocIdPendingYourQuotation : nextPageDocIdPendingApproval;
    const initialLoadedFlag = quoteStatus === 'pending-quote' ? initialPendingYourQuotationLoaded : initialPendingApprovalLoaded;
    const setInitialLoadedFlag = quoteStatus === 'pending-quote' ? setInitialPendingYourQuotationLoaded : setInitialPendingApprovalLoaded;


    isLoadingSetter(true);
    errorSetter(null);

    try {
      const options = {
        fseId: userInfo.fseId,
        quoteStatus: quoteStatus,
        pageSize: 10,
        orderBy: 'startDate',
        orderDirection: 'asc',
        startAfterDocId: loadMore ? currentNextPageDocId : null,
      };
      const data = await listJobSchedules(options);

      schedulesSetter(prevSchedules =>
        loadMore ? [...prevSchedules, ...(data.schedules || [])] : (data.schedules || [])
      );
      nextPageDocIdSetter(data.schedules && data.schedules.length >= 10 ? data.nextPageDocId : null);
    } catch (err) {
      console.error(`Failed to fetch ${quoteStatus} schedules:`, err);
      errorSetter(err.message || `Could not load ${quoteStatus} schedules.`);
      if (!loadMore) schedulesSetter([]);
    } finally {
      isLoadingSetter(false);
      if (!initialLoadedFlag) { // Set initial loaded flag only once after the first fetch attempt
        setInitialLoadedFlag(true);
      }
    }
  }, [userInfo, nextPageDocIdPendingYourQuotation, nextPageDocIdPendingApproval]); // Removed initialLoaded flags from here as they are set inside

  // Fetch Pending Quote Schedules Effect (renamed to fetchSchedulesByQuoteStatus)
  // const fetchPendingQuotes = useCallback(async (loadMore = false) => { // This function is now fetchSchedulesByQuoteStatus
  // ...
  // }, [userInfo, nextPageDocIdPendingQuotes, initialPendingQuotesLoaded]);


  useEffect(() => {
    if (userInfo) {
      // console.log("useEffect triggered: Fetching jobs for user:", userInfo.uid, "Type:", userInfo.userType);
      // This is an initial load or a load due to filter changes. loadMore is false.
      fetchJobs({ status: selectedStatus, householdId: filterHouseholdId }, false);
    } else {
      // console.log("useEffect triggered: userInfo not available yet.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo, selectedStatus, filterHouseholdId]); // fetchJobs is intentionally omitted from deps here

  // useEffect for preloading 'pending-quote' for the welcome message
  useEffect(() => {
    if (userInfo && !initialPendingYourQuotationLoaded) {
      fetchSchedulesByQuoteStatus('pending-quote', false);
    }
  }, [userInfo, initialPendingYourQuotationLoaded, fetchSchedulesByQuoteStatus]);

  // Fetch welcome message counts via aggregation endpoint — 2 Firestore reads total
  useEffect(() => {
    if (!userInfo || !userInfo.fseId) return;
    const fetchWelcomeCounts = async () => {
      try {
        const counts = await getJobCounts({ fseId: userInfo.fseId });
        setWelcomeApprovedCount(counts.approvedJobCount);
        setWelcomePendingQuoteCount(counts.pendingQuoteCount);
      } catch (err) {
        console.error('Failed to fetch job counts for welcome message:', err);
        setWelcomeApprovedCount(0);
        setWelcomePendingQuoteCount(0);
      }
    };
    fetchWelcomeCounts();
  }, [userInfo]);

  // useEffect for fetching data based on active MAIN tab and active SUB tab for Quotes
  useEffect(() => {
    if (userInfo && activeTab === 'pendingQuotes') {
      if (activeQuoteSubTab === 'pendingYourQuotation' && !initialPendingYourQuotationLoaded) {
        fetchSchedulesByQuoteStatus('pending-quote', false);
      } else if (activeQuoteSubTab === 'pendingApproval' && !initialPendingApprovalLoaded) {
        fetchSchedulesByQuoteStatus('pending-approval', false);
      }
    }
  }, [userInfo, activeTab, activeQuoteSubTab, initialPendingYourQuotationLoaded, initialPendingApprovalLoaded, fetchSchedulesByQuoteStatus]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterDropdownRef.current && !filterDropdownRef.current.contains(event.target)) {
        householdSearch.setShowSuggestions(false);
      }
    };
    if (householdSearch.showSuggestions) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    };
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [householdSearch.showSuggestions, householdSearch]);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      setUserInfo(null);
      setJobs([]);
      setInitialJobsLoaded(false);
      setPendingYourQuotationSchedules([]);
      setNextPageDocIdPendingYourQuotation(null);
      setInitialPendingYourQuotationLoaded(false);
      setPendingApprovalSchedules([]);
      setNextPageDocIdPendingApproval(null);
      setInitialPendingApprovalLoaded(false);
      setUserError(null);
      setJobsError(null);
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      setUserError('Failed to logout.');
    }
  };

  const handleStatusFilterChange = (event) => {
    setSelectedStatus(event.target.value);
  };

  // --- Filter Handlers ---
  const handleClearFilters = () => {
    setSelectedStatus(''); // Reset status to empty string on clear
    setFilterHouseholdId(null);
    setHouseholdSearchTerm('');
    householdSearch.setSearchTerm('');
    householdSearch.setSuggestions([]);
    sessionStorage.removeItem('home_filter_status');
    sessionStorage.removeItem('home_filter_householdId');
    sessionStorage.removeItem('home_filter_householdSearchTerm');
  };

  const householdFilterHandlers = {
    onChange: (e) => {
      const value = e.target.value;
      householdSearch.setSearchTerm(value);
      setHouseholdSearchTerm(value);
      setFilterHouseholdId(null);
      if (value.length < 3) householdSearch.setShowSuggestions(false);
    },
    onSelect: (item) => {
      setFilterHouseholdId(item.id);
      const displayName = `${item.firstName || ''} ${item.lastName || ''}`.trim() || 'N/A';
      const displayAddress = item.addressLine1 ? ` (${item.addressLine1})` : '';
      setHouseholdSearchTerm(`${displayName}${displayAddress}`);
      householdSearch.setSuggestions([]);
      householdSearch.setShowSuggestions(false);
    },
    renderSuggestions: (item) => {
      const displayName = `${item.firstName || ''} ${item.lastName || ''}`.trim() || 'N/A';
      const displayAddress = item.addressLine1 ? ` (${item.addressLine1})` : '';
      return `${displayName}${displayAddress}`;
    },
  };
  // --- End Filter Handlers ---

  const handleLoadMorePendingYourQuotation = () => {
    if (nextPageDocIdPendingYourQuotation && !loadingPendingYourQuotation) {
      fetchSchedulesByQuoteStatus('pending-quote', true);
    }
  };

  const handleLoadMorePendingApproval = () => {
    if (nextPageDocIdPendingApproval && !loadingPendingApproval) {
      fetchSchedulesByQuoteStatus('pending-approval', true);
    }
  };

  // Function to load more jobs in the My Jobs tab
  const handleLoadMoreJobs = () => {
    if (nextPageDocId && !loadingJobs) {
      fetchJobs({}, true);
    }
  };

  // Helper function to render schedule date for pending quotes
  const renderScheduleDetails = (schedule) => {
    if (!schedule || !schedule.startDate) return 'N/A';
    return formatTimestamp(schedule.startDate);
  };

  const handleQuoteSubmit = async (scheduleId, pricePerHour) => {
    // Get frequency data for this schedule
    const frequencyData = frequencyInputs[scheduleId] || {};
    
    // Show confirmation modal instead of submitting directly
    setQuoteToConfirm({ 
      scheduleId, 
      pricePerHour, 
      notesFromFranchisee: notesForAdminInputs[scheduleId],
      frequency: frequencyData
    });
    setShowQuoteConfirmation(true);
  };

  const confirmAndSubmitQuote = async () => {
    if (!quoteToConfirm) return;
    
    const { scheduleId, pricePerHour, notesFromFranchisee, frequency } = quoteToConfirm;
    setQuoteSubmitError(prev => ({ ...prev, [scheduleId]: null })); 
    setIsSubmittingQuote(prev => ({ ...prev, [scheduleId]: true })); 
    setShowQuoteConfirmation(false); // Hide modal

    try {
      // Prepare frequency data for API
      let frequencyType = null;
      let frequencyValue = null;
      
      if (frequency && frequency.option === 'monthly') {
        frequencyType = 'monthly';
      } else if (frequency && frequency.option === 'interval' && frequency.intervalValue && frequency.intervalUnit) {
        // Validate interval value
        const intervalValue = parseInt(frequency.intervalValue);
        if (intervalValue && intervalValue > 0 && Number.isInteger(intervalValue)) {
          frequencyType = frequency.intervalUnit === 'days' ? 'days' : 'weeks';
          frequencyValue = intervalValue;
        }
      }

      console.log('Submitting quote with frequency data:', { scheduleId, pricePerHour, notesFromFranchisee, frequencyType, frequencyValue });

      await submitQuote(scheduleId, pricePerHour, notesFromFranchisee, frequencyType, frequencyValue);
      fetchSchedulesByQuoteStatus('pending-quote', false);
      setPricePerHourInputs(prev => ({ ...prev, [scheduleId]: '' }));
      setNotesForAdminInputs(prev => ({ ...prev, [scheduleId]: '' }));
      setFrequencyInputs(prev => ({ ...prev, [scheduleId]: { option: '', intervalValue: '', intervalUnit: 'days' } }));
    } catch (error) {
      console.error('Failed to submit quote:', error);
      setQuoteSubmitError(prev => ({ 
        ...prev, 
        [scheduleId]: error.message || 'Failed to submit quote. Please try again.' 
      }));
    } finally {
      setIsSubmittingQuote(prev => ({ ...prev, [scheduleId]: false }));
      setQuoteToConfirm(null);
    }
  };

  // Show tabs once initial data load completes - don't hide tabs based on filter results
  const hasAnyData = initialJobsLoaded || pendingYourQuotationSchedules.length > 0 || pendingApprovalSchedules.length > 0;
  const showJobTabs = hasAnyData;

  useEffect(() => {
    if (!showJobTabs) {
      setActiveTab('compliance');
    } else {
      setActiveTab('myJobs'); 
    }
  }, [showJobTabs]);

  // Add handlers for tab changes
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'myJobs') {
      fetchJobs({ status: selectedStatus, householdId: filterHouseholdId }, false);
    } else if (tab === 'pendingQuotes') {
      // Reset to default sub-tab and fetch its data
      setActiveQuoteSubTab('pendingYourQuotation');
      fetchSchedulesByQuoteStatus('pending-quote', false);
    }
  };

  const handleQuoteSubTabChange = (subTab) => {
    setActiveQuoteSubTab(subTab);
    if (subTab === 'pendingYourQuotation') {
      fetchSchedulesByQuoteStatus('pending-quote', false);
    } else if (subTab === 'pendingApproval') {
      fetchSchedulesByQuoteStatus('pending-approval', false);
    }
  };

  if (loadingUser || loadingConfig) { // Check for config loading as well
    return <div className="container mx-auto px-4 py-16 text-center">Loading user data...</div>;
  }

  if (userError || configError) { // Check for config error
    return <div className="container mx-auto px-4 py-16 text-center text-red-600">Error: {userError || configError}</div>;
  }

  return (
    <div className="container mx-auto px-4 py-8 md:py-16">
      <div className="max-w-4xl mx-auto">
        <div className="flex justify-between items-center mb-6 md:mb-8">
          <h1 className="text-xl md:text-3xl font-extrabold text-gray-900">
            Loope Franchisee App
          </h1>
          <button
            onClick={handleLogout}
            className="px-3 py-1.5 md:px-4 md:py-2 text-xs md:text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-full shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
          >
            <FaSignOutAlt className="inline md:hidden" />
            <span className="hidden md:inline">Logout</span>
          </button>
        </div>

        {/* Updated Welcome Message Styling */}
        <div className="mb-6 md:mb-8">
          <p className="text-base md:text-xl text-gray-700 text-left">Welcome, {(userInfo?.firstName || userInfo?.franchiseeData?.firstName || 'Franchisee')}!</p>
          {userInfo && initialPendingYourQuotationLoaded && (
            <p className="text-sm text-gray-600 mt-1">
              You have {welcomeApprovedCount ?? '...'} approved job(s){welcomePendingQuoteCount > 0 ? ` and ${welcomePendingQuoteCount} job(s) pending quotation. Please submit your quote(s) as soon as possible.` : '.'}
            </p>
          )}
        </div>

        {/* Tabs Navigation */}
        <div className="mb-4 border-b border-gray-200">
          <nav className="-mb-px flex space-x-8" aria-label="Tabs">
            {showJobTabs && (
              <>
                <button
                  onClick={() => handleTabChange('myJobs')}
                  className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'myJobs'
                      ? 'border-green-500 text-green-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  My Jobs
                </button>
                <button
                  onClick={() => handleTabChange('pendingQuotes')}
                  className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'pendingQuotes'
                      ? 'border-green-500 text-green-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Quote Pending
                </button>
              </>
            )}
            <button
              onClick={() => handleTabChange('compliance')}
              className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'compliance'
                  ? 'border-green-500 text-green-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Compliance
            </button>
          </nav>
        </div>

        {activeTab === 'myJobs' && (
          <>
            <div ref={filterDropdownRef} className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4 items-end"> {/* Added mb-4 for spacing */}
              <div>
                <label htmlFor="status-filter" className="block text-xs font-medium text-gray-600">
                  Status
                </label>
                <select
                  id="status-filter"
                  name="status-filter"
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-sm md:text-base border-gray-300 focus:outline-none focus:ring-green-500 focus:border-green-500 rounded-md shadow-sm"
                  value={selectedStatus}
                  onChange={handleStatusFilterChange}
                  disabled={loadingJobs || loadingConfig} // Disable while loading config too
                >
                  {/* Use memoized status options */}
                  {statusFilterOptions.map((statusOption) => (
                    <option key={statusOption.value} value={statusOption.value}>
                      {statusOption.label}
                    </option>
                  ))}
                </select>
              </div>

              {true && (
              <div className="relative">
                <label htmlFor="householdFilter" className="block text-xs font-medium text-gray-600">
                  {terminology.household.singular}
                </label>
                <input
                  id="householdFilter"
                  type="text"
                  autoComplete="off"
                  value={householdSearchTerm}
                  onChange={householdFilterHandlers.onChange}
                  onBlur={() => setTimeout(() => householdSearch.setShowSuggestions(false), 150)}
                  onFocus={() => householdSearch.suggestions.length > 0 && householdSearch.setShowSuggestions(true)}
                  placeholder="Type to search..."
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm md:text-base focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  disabled={loadingJobs}
                />
                {householdSearch.showSuggestions && (
                  <ul className="absolute z-10 w-full bg-white border border-gray-300 rounded-md mt-1 max-h-60 overflow-auto shadow-lg">
                    {householdSearch.isSearching ? (
                      <li className="px-3 py-2 text-sm text-gray-500">Searching...</li>
                    ) : householdSearch.suggestions.length > 0 ? (
                      householdSearch.suggestions.map((item) => (
                        <li
                          key={item.id}
                          className="px-3 py-2 text-sm text-gray-700 cursor-pointer hover:bg-gray-100"
                          onMouseDown={() => householdFilterHandlers.onSelect(item)}
                        >
                          {householdFilterHandlers.renderSuggestions(item)}
                        </li>
                      ))
                    ) : (
                      householdSearch.searchTerm.length >= 3 && (
                        <li className="px-3 py-2 text-sm text-gray-500">
                          No {terminology.household.plural.toLowerCase()} found.
                        </li>
                      )
                    )}
                  </ul>
                )}
              </div>
              )}

              {true && (
              <div className="flex justify-end">
                <button
                  onClick={handleClearFilters}
                  className="px-3 py-1.5 text-xs md:text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-full shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400 disabled:opacity-50"
                  disabled={loadingJobs}
                >
                  <FaTimes className="inline mr-1" /> Clear Filters
                </button>
              </div>
              )}
            </div>

            {/* Initial loading message: Show only if no jobs are displayed yet and not an error state */}
            {loadingJobs && jobs.length === 0 && !jobsError && (
              <p className="text-center text-gray-500 py-5">Loading jobs...</p>
            )}
            {/* Error message: Show if an error occurred and no jobs are displayed */}
            {jobsError && jobs.length === 0 && (
              <p className="text-center text-red-500 bg-red-100 p-3 rounded">Error loading jobs: {jobsError}</p>
            )}

              {/* Job list container: Render if jobs exist, or if not loading and no error (for "no jobs found") */}
              {/* This ensures existing jobs remain visible during "load more" */}
              {(jobs.length > 0 || (!loadingJobs && !jobsError)) && (
                <div className="space-y-4">
                  {displayedJobs.length === 0 ? (
                    // This renders if displayedJobs.length is 0, !loadingJobs, and !jobsError
                    <p className="text-center text-gray-500 p-6 bg-gray-50 rounded shadow-sm">
                      {`No jobs found ${
                        filterHouseholdId || selectedStatus !== ''
                          ? 'matching your filters'
                          : `with status: ${statusFilterOptions.find(s => s.value === selectedStatus)?.label || 'All Statuses'}`
                      }.`}
                    </p>
                  ) : (
                    displayedJobs.map((job) => {
                      const isFixedFee = job.allocationType === 'fixed-fee';
                      let allottedHoursLabel = 'Allotted';
                      if (job.allocationType === 'monthly') {
                        allottedHoursLabel = 'Allocation per month';
                      } else if (job.allocationType === 'per-visit') {
                        allottedHoursLabel = 'Allocation per visit';
                      } else if (isFixedFee) {
                        allottedHoursLabel = 'Fixed Hours'; // Optional label change
                      }

                      // --- Overdue Check ---
                      let jobDateAsDate = null;
                      if (job.jobDate) {
                        if (typeof job.jobDate.toDate === 'function') {
                          // Handle Firestore Timestamp objects directly
                          jobDateAsDate = job.jobDate.toDate();
                        } else if (typeof job.jobDate._seconds === 'number') {
                          // Handle serialized Timestamp objects { _seconds, _nanoseconds }
                          jobDateAsDate = new Date(job.jobDate._seconds * 1000);
                        }
                      }

                      const todayStart = new Date(new Date().setHours(0, 0, 0, 0)); // Define today's start date for comparison
                      const isOverdue = jobDateAsDate && jobDateAsDate < todayStart;

                      // --- End Overdue Check ---

                      // --- Generate Google Maps URL ---
                      const googleMapsUrl = getGoogleMapsUrl(job.householdInfo);
                      const hasAddress = job.householdInfo?.addressLine1 || job.householdInfo?.addressLine2 || job.householdInfo?.state || job.householdInfo?.postalCode;
                      const addressText = (() => {
                        const raw = hasAddress
                          ? `${job.householdInfo?.addressLine1 || ''}${
                              job.householdInfo?.addressLine2 ? ', ' + job.householdInfo.addressLine2 : ''
                            }, ${job.householdInfo?.state || ''} ${job.householdInfo?.postalCode || ''}`
                            .replace(/^, /, '')
                            .replace(/, $/, '')
                            .trim()
                          : 'No address';
                        return raw;
                      })();
                      // --- End Google Maps URL Generation ---

                      // --- Determine Rate/Quote to display ---
                      const isPendingQuoteOrApproval = job.status === 'pending-quote' || job.status === 'pending-approval';
                      let rateDisplay = 'N/A';
                      if (isPendingQuoteOrApproval && job.priceQuotePerHour != null) {
                        rateDisplay = `$${job.priceQuotePerHour.toFixed(2)} / hr (Quote Incl. GST)`;
                      } else if (isFixedFee && job.pricePerHour != null && job.allotedHours != null) {
                        rateDisplay = `$${(job.pricePerHour * job.allotedHours).toFixed(2)} (Fixed Incl. GST)`;
                      } else if (job.pricePerHour != null) {
                        rateDisplay = `$${job.pricePerHour.toFixed(2)} / hr (Incl. GST)`;
                      }
                      // --- End Rate/Quote determination ---

                      return (
                        <Link key={job.id} to={`/job/${job.id}`} className="block hover:bg-gray-50 rounded-2xl transition duration-150 ease-in-out">
                          <div className="bg-white shadow-sm ring-1 ring-gray-900/5 rounded-2xl p-4 relative">
                            {/* --- Overdue Message --- */}
                            {/* Show only if overdue AND status is approved */}
                            {isOverdue && job.status === 'approved' && (
                              <p className="text-xs text-red-600 mb-2 pb-2 border-b border-red-200 flex items-center">
                                <FaExclamationTriangle className="mr-1" />
                                Job is overdue. Reschedule or update to 'Completed' if the job is done.
                              </p>
                            )}
                            {/* --- End Overdue Message --- */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600 mb-3 pb-3 border-b border-gray-300">
                              <div>
                                <p className="font-semibold mb-1">
                                  <FaHome className="inline mr-1 text-gray-500" />
                                  {terminology.household.singular}
                                </p>
                                <p>{`${job.householdInfo?.firstName || ''} ${job.householdInfo?.lastName || ''}`.trim() || 'N/A'}</p>
                                {/* Updated Address Display */}
                                <div className="text-xs text-gray-500 flex items-start">
                                  <FaMapMarkerAlt className="inline mr-1 mt-0.5 flex-shrink-0" />
                                  {googleMapsUrl ? (
                                    <a
                                      href={googleMapsUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-blue-600 hover:underline"
                                      title="Open address in Google Maps"
                                      onClick={(e) => e.stopPropagation()} // Prevent Link navigation when clicking address
                                    >
                                      {addressText}
                                    </a>
                                  ) : (
                                    <span>{addressText}</span>
                                  )}
                                </div>
                                {/* End Updated Address Display */}
                              </div>
                              <div>
                                <p className="font-semibold mb-1">
                                  <FaUser className="inline mr-1 text-gray-500" />
                                  {terminology.client.singular}
                                </p>
                                <p>{(job.clientInfo?.clientName || 'N/A')}</p>
                              </div>
                            </div>

                            <div className="text-sm text-gray-700 flex-grow">
                              <div className="flex items-center space-x-2 mb-1.5">
                                <FaRedo className="text-gray-500" />
                                <span className="font-medium text-gray-600">Status:</span>
                                <StatusBadge
                                  statusCode={job.status}
                                  label={job.status === 'completed' && job.completionDate
                                    ? `Completed on ${formatTimestamp(job.completionDate)}`
                                    : undefined
                                  }
                                />
                              </div>
                              {job.status === 'service-delayed' && job.reasonForDelay && (
                                <p className="text-sm text-orange-700 bg-orange-100 p-1 rounded border border-orange-200 mb-1.5">
                                  <FaInfoCircle className="inline mr-1 text-orange-500" />
                                  Delay Reason: {job.reasonForDelay}
                                </p>
                              )}
                              {/* Row 1: Schedule */}
                              <div className="flex flex-col sm:flex-row sm:items-center sm:gap-x-4 gap-y-1 mb-1">
                                <p className={isOverdue && job.status === 'approved' ? 'text-red-600' : ''}>
                                  <FaCalendarAlt className="inline mr-1 text-green-600" />
                                  Scheduled on: <span className={job.status === 'approved' ? 'font-bold' : ''}>{formatTimestamp(job.jobDate)}</span>
                                </p>
                                <p>
                                  <FaClock className="inline mr-1 text-green-600" />
                                  Time: {job.jobTime || 'N/A'}
                                  {job.scheduleType && (
                                    <span className="ml-3 capitalize">
                                      <FaCalendarCheck className="inline mr-1 text-blue-600" />
                                      {job.scheduleType === 'recurring' && job.frequencyType
                                        ? formatFrequency(job.frequencyType, job.frequencyValue)
                                        : job.scheduleType.replace('-', ' ')
                                      }
                                    </span>
                                  )}
                                </p>
                              </div>
                              {/* Row 2: Allocation + Actual */}
                              <div className="flex flex-col sm:flex-row sm:items-center sm:gap-x-4 gap-y-1 mb-1">
                                <p>
                                  <FaHourglassHalf className="inline mr-1 text-green-600" />
                                  Allocation: {formatAllocationDetails(job)}
                                </p>
                                {!isFixedFee && (
                                  <p>
                                    <FaHourglassHalf className="inline mr-1 text-green-600" />
                                    Actual: {job.actualHours != null ? `${job.actualHours} hr(s)` : 'N/A'}
                                  </p>
                                )}
                              </div>
                              {/* Row 3: Rate */}
                              <p className="mb-1">
                                <FaDollarSign className="inline mr-1 text-green-600" />
                                Rate: {rateDisplay}
                              </p>
                              {job.services && job.services.length > 0 && (
                                <p className="text-sm">
                                  <FaTasks className="inline mr-2 text-green-600" />
                                  Services: {job.services?.map(s => s.name).join(', ')}
                                </p>
                              )}
                              {job.isNoPhotoJob && (
                                <p className="text-xs text-gray-600 italic">
                                  <MdOutlineImageNotSupported className="inline mr-2 text-gray-500" />
                                  No photos required for this job
                                </p>
                              )}
                              {job.remarks && (
                                <p className="text-sm mt-1.5 pt-1.5 border-t border-gray-200">
                                  <FaCommentAlt className="inline mr-2 text-gray-500" />
                                  Remarks: {job.remarks}
                                </p>
                              )}
                            </div>
                          </div>
                        </Link>
                      );
                    })
                  )}

                  {/* Load More Button / Loading More Indicator / No More Jobs Message / Retry for My Jobs */}
                  {activeTab === 'myJobs' && (
                    <>
                      {/* "Loading more jobs..." indicator: shown if loading, jobs exist, and no overriding error */}
                      {loadingJobs && jobs.length > 0 && !jobsError && (
                        <p className="text-center text-gray-500 mt-4 py-5">Loading more jobs...</p>
                      )}

                      {/* Load More Button: shown if not loading, there's a next page, no "no more" message, and no error */}
                      {!loadingJobs && nextPageDocId && !noMoreJobsMessage && !jobsError && (
                        <div className="mt-6 text-center">
                          <button
                            onClick={handleLoadMoreJobs}
                            className="px-6 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-full shadow-sm hover:bg-green-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                          >
                            Load More Jobs
                          </button>
                        </div>
                      )}

                      {/* "No more jobs to load" message: shown if not loading, message exists, and no error */}
                      {!loadingJobs && noMoreJobsMessage && !jobsError && (
                        <p className="text-center text-gray-500 mt-4 py-5">{noMoreJobsMessage}</p>
                      )}
                      
                      {/* Error and Retry for "Load More" if jobs were already displayed */}
                      {jobsError && jobs.length > 0 && (
                        <div className="mt-4 text-center">
                            <p className="text-red-500 bg-red-100 p-3 rounded mb-2">Error loading more jobs: {jobsError}</p>
                            {/* Offer retry only if it seemed like there was more to load (nextPageDocId was likely present before error) */}
                            {/* fetchJobs doesn't nullify nextPageDocId on error if loadMore is true, so it should reflect pre-error state */}
                            {nextPageDocId && ( 
                                <button
                                    onClick={() => {
                                        // fetchJobs will clear jobsError internally when called by handleLoadMoreJobs
                                        handleLoadMoreJobs(); 
                                    }}
                                    className="px-6 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-full shadow-sm hover:bg-red-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                                >
                                    Retry Load More
                                </button>
                            )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
          </>
        )}

        {activeTab === 'pendingQuotes' && (
          <div className="bg-white rounded-2xl ring-1 ring-gray-900/5 shadow-sm p-4 md:p-8">
            {/* Sub Tabs Navigation for Pending Quotes */}
            <div className="mb-4 border-b border-gray-200">
              <nav className="-mb-px flex space-x-8" aria-label="Quote Sub Tabs">
                <button
                  onClick={() => handleQuoteSubTabChange('pendingYourQuotation')}
                  className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm ${
                    activeQuoteSubTab === 'pendingYourQuotation'
                      ? 'border-green-600 text-green-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Pending Your Quotation
                </button>
                <button
                  onClick={() => handleQuoteSubTabChange('pendingApproval')}
                  className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm ${
                    activeQuoteSubTab === 'pendingApproval'
                      ? 'border-green-600 text-green-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Pending Approval
                </button>
              </nav>
            </div>

            {/* Content for "Pending Your Quotation" sub-tab */}
            {activeQuoteSubTab === 'pendingYourQuotation' && (
              <PendingQuotationList
                schedules={pendingYourQuotationSchedules}
                loading={loadingPendingYourQuotation}
                error={pendingYourQuotationError}
                initialLoaded={initialPendingYourQuotationLoaded}
                pricePerHourInputs={pricePerHourInputs}
                notesForAdminInputs={notesForAdminInputs}
                frequencyInputs={frequencyInputs}
                isSubmittingQuote={isSubmittingQuote}
                quoteSubmitError={quoteSubmitError}
                nextPageDocId={nextPageDocIdPendingYourQuotation}
                onPricePerHourChange={handlePricePerHourChange}
                onNotesForAdminChange={handleNotesForAdminChange}
                onFrequencyChange={handleFrequencyChange}
                onQuoteSubmit={handleQuoteSubmit}
                onLoadMore={handleLoadMorePendingYourQuotation}
              />
            )}

            {/* Content for "Pending Approval" sub-tab */}
            {activeQuoteSubTab === 'pendingApproval' && (
              <>
                {loadingPendingApproval && pendingApprovalSchedules.length === 0 && <p className="text-center text-gray-500 py-5">Loading schedules pending approval...</p>}
                {pendingApprovalError && <p className="text-center text-red-500 bg-red-100 p-3 rounded">Error: {pendingApprovalError}</p>}
                {!loadingPendingApproval && !pendingApprovalError && pendingApprovalSchedules.length === 0 && initialPendingApprovalLoaded && (
                  <p className="text-center text-gray-500 py-5">No jobs found pending approval.</p>
                )}
                {pendingApprovalSchedules.length > 0 && (
                  <div className="space-y-4">
                    {pendingApprovalSchedules.map((schedule) => {
                      const isFixedFee = schedule.allocationType === 'fixed-fee';
                      let allottedHoursLabel = 'Allotted';
                      if (schedule.allocationType === 'monthly') {
                        allottedHoursLabel = 'Allocation per month';
                      } else if (schedule.allocationType === 'per-visit') {
                        allottedHoursLabel = 'Allocation per visit';
                      } else if (isFixedFee) {
                        allottedHoursLabel = 'Fixed Hours'; // Corrected this line
                      }
                      return (
                        <div key={schedule.id} className="bg-white shadow-sm ring-1 ring-gray-900/5 rounded-2xl p-4">
                          {/* Row 1: Client and Household Info */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600 mb-3 pb-3 border-b">
                            <div>
                              <p className="font-semibold mb-1"><FaHome className="inline mr-1 text-gray-500"/>{terminology.household.singular}</p>
                              <p>{(`${schedule.householdInfo?.firstName || ''} ${schedule.householdInfo?.lastName || ''}`.trim() || 'N/A')}</p>
                              <p className="text-xs text-gray-500"><FaMapMarkerAlt className="inline mr-1"/>{(() => { const raw = `${schedule.householdInfo?.addressLine1 || ''}${schedule.householdInfo?.state ? ', ' + schedule.householdInfo.state : ''} ${schedule.householdInfo?.postalCode || ''}`.replace(/^, /,'').replace(/, $/,'').trim() || 'No address'; return raw; })()}</p>
                            </div>
                            <div>
                              <p className="font-semibold mb-1"><FaUser className="inline mr-1 text-gray-500"/>{terminology.client.singular}</p>
                              <p>{(schedule.clientInfo?.clientName || 'N/A')}</p>
                            </div>
                          </div>
                          {/* Row 2: Schedule Details and Time */}
                          <div className="text-sm text-gray-700 space-y-1 mb-3">
                              <p className="font-semibold capitalize"><FaCalendarAlt className="inline mr-2 text-green-600" />{formatTimestamp(schedule.startDate)}
                                {schedule.jobTime && (<span className="ml-3"><FaClock className="inline mr-1 text-green-600" />Preferred Time: {schedule.jobTime}</span>)}
                              </p>
                          </div>
                          {/* Row 3: Allocation, Hours, Services, Notes, and Submitted Price */}
                          <div className="text-sm text-gray-700 space-y-1 mb-3">
                              <p>
                                  {schedule.allocationType && (<span className="mr-3 inline-flex items-center"><FaLayerGroup className="inline mr-1 text-green-600" />Allocation: {formatAllocationType(schedule.allocationType)}</span>)}
                                  {schedule.allotedHours != null && (<span className="mr-3 inline-flex items-center"><FaHourglassHalf className="mr-1 text-green-600" />
                                    {allottedHoursLabel}: {schedule.allotedHours} hr(s)</span>)}
                              </p>
                              {schedule.services && schedule.services.length > 0 && (<p><FaTasks className="inline mr-1 text-green-600" />Services: {schedule.services.map(s => s.name).join(', ')}</p>)}
                              {schedule.notesForFranchisee && (<p><FaStickyNote className="inline mr-1 text-green-600" />Notes: <span className="text-gray-600 italic">{schedule.notesForFranchisee}</span></p>)}
                              {schedule.pricePerHour != null && ( // Corrected: Moved <p> inside this condition and fixed structure
                                <p className="mt-2 pt-2 border-t border-gray-200">
                                  <FaDollarSign className="inline mr-1 text-jims-green" />
                                  Submitted Quote: <span className="font-semibold">${schedule.pricePerHour.toFixed(2)} (Incl. GST)
                                  {schedule.allocationType !== 'fixed-fee' ? ' / hr' : ' (Fixed Total)'}</span>
                                </p>
                              )}
                          </div>
                          {schedule.description && (<div className="text-sm text-gray-700 space-y-1 mt-3"><p><FaInfoCircle className="inline mr-2 text-green-600" />Description: <span className="text-gray-600">{schedule.description}</span></p></div>)}
                        </div>
                      );
                    })}
                    {nextPageDocIdPendingApproval && ( // Corrected: Button directly inside this condition
                      <div className="mt-6 text-center">
                        <button onClick={handleLoadMorePendingApproval} disabled={loadingPendingApproval} className="px-6 py-2 text-sm font-medium text-white bg-green-600 rounded-full hover:bg-green-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50">
                          {loadingPendingApproval ? 'Loading...' : 'Load More Pending Approval'}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        )}

         {/* Compliance Tab */}
        {activeTab === 'compliance' && (
          <ComplianceTab userInfo={userInfo} />
        )}
      </div>


     

      {/* Add Quote Confirmation Modal */}
      {showQuoteConfirmation && quoteToConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-md">
            <h4 className="text-lg font-semibold mb-4 text-gray-800">Confirm Quote Submission</h4>
            <p className="text-sm text-gray-700 mb-4">
              Are you sure you want to submit this quote?
              <br />
              <span className="font-semibold">
                Quote Amount: ${quoteToConfirm.pricePerHour.toFixed(2)} (Incl. GST)
                {pendingYourQuotationSchedules.find(s => s.id === quoteToConfirm.scheduleId)?.allocationType !== 'fixed-fee' ? ' / hr' : ' (Fixed Total)'}
              </span>
            </p>
            {/* GST Information Alert */}
            <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mb-4">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-blue-700">
                    <strong>Important:</strong> This quotation includes GST. Please ensure your pricing reflects the total amount including GST.
                  </p>
                </div>
              </div>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowQuoteConfirmation(false);
                  setQuoteToConfirm(null);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-full shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400"
              >
                Cancel
              </button>
              <button
                onClick={confirmAndSubmitQuote}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-full hover:bg-green-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 flex items-center"
              >
                Submit Quote
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;
